/**
 * Placeholder Element Renderers
 * All placeholder elements that show dynamic data
 */

import { BaseElementRenderer, type ElementRenderContext } from './base-element-renderer';

// Invoice Data Placeholders (Blue)
export class InvoiceNumberRenderer extends BaseElementRenderer {
  canRender(elementType: string): boolean {
    return elementType === 'invoice-number';
  }
  
  render(context: ElementRenderContext) {
    return this.renderPlaceholder(context, '{Rechnungsnummer}', '#eff6ff', '#bfdbfe', '#2563eb');
  }
}

export class InvoiceDateRenderer extends BaseElementRenderer {
  canRender(elementType: string): boolean {
    return elementType === 'invoice-date';
  }
  
  render(context: ElementRenderContext) {
    return this.renderPlaceholder(context, '{Rechnungsdatum}', '#eff6ff', '#bfdbfe', '#2563eb');
  }
}

export class PaymentDueDateRenderer extends BaseElementRenderer {
  canRender(elementType: string): boolean {
    return elementType === 'payment-due-date';
  }
  
  render(context: ElementRenderContext) {
    const format = 'paymentDueDateFormat' in context.element ? context.element.paymentDueDateFormat : undefined;
    const text = format === 'text' 
      ? '{Zahlungsziel Text}' 
      : '{Zahlungsziel Datum}';
    return this.renderPlaceholder(context, text, '#eff6ff', '#bfdbfe', '#2563eb');
  }
}

export class OrderNumberRenderer extends BaseElementRenderer {
  canRender(elementType: string): boolean {
    return elementType === 'order-number';
  }
  
  render(context: ElementRenderContext) {
    return this.renderPlaceholder(context, '{Bestellnummer}', '#eff6ff', '#bfdbfe', '#2563eb');
  }
}

export class LeitwegIdRenderer extends BaseElementRenderer {
  canRender(elementType: string): boolean {
    return elementType === 'leitweg-id';
  }
  
  render(context: ElementRenderContext) {
    return this.renderPlaceholder(context, '{Leitweg-ID}', '#eff6ff', '#bfdbfe', '#2563eb');
  }
}

export class PageNumberRenderer extends BaseElementRenderer {
  canRender(elementType: string): boolean {
    return elementType === 'page-number';
  }
  
  render(context: ElementRenderContext) {
    const format = ('pageNumberFormat' in context.element ? context.element.pageNumberFormat : undefined) || 'page-x';
    let displayText = '';
    
    switch (format) {
      case 'page-x':
        displayText = 'Seite X';
        break;
      case 'page-x-of-y':
        displayText = 'Seite X von Y';
        break;
      case 'x':
        displayText = 'X';
        break;
      case 'x-slash-y':
        displayText = 'X/Y';
        break;
      case 'x-dash-y':
        displayText = 'X-Y';
        break;
      default:
        displayText = 'Seite X';
    }
    
    return this.renderPlaceholder(context, displayText, '#f3f4f6', '#d1d5db', '#4b5563');
  }
}

export class SmallBusinessNoteRenderer extends BaseElementRenderer {
  canRender(elementType: string): boolean {
    return elementType === 'small-business-note';
  }
  
  render(context: ElementRenderContext) {
    return (
      <div 
        style={{
          ...context.elementStyle,
          backgroundColor: '#fef3c7',
          border: '1px solid #fcd34d',
          borderRadius: '4px',
          fontStyle: 'italic',
        }} 
        className="whitespace-pre-wrap text-gray-700" 
        onClick={context.onClick}
      >
        {('content' in context.element ? context.element.content : undefined) || 'Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.'}
      </div>
    );
  }
}

// Address Placeholders (Green)
export class SenderRenderer extends BaseElementRenderer {
  canRender(elementType: string): boolean {
    return elementType === 'sender';
  }
  
  render(context: ElementRenderContext) {
    const displayMode = (context.element as any).displayMode || 'block';
    const isInline = displayMode === 'inline';
    const fontWeight = (context.element as any).fontWeight || 'normal';
    const fontStyle = (context.element as any).fontStyle || 'normal';
    const fontDecoration = (context.element as any).fontDecoration || 'none';
    
    return (
      <div 
        style={{
          ...context.elementStyle,
          backgroundColor: '#f0fdf4',
          border: '1px solid #86efac',
          borderRadius: '4px',
        }} 
        onClick={context.onClick}
      >
        <div style={{ color: '#16a34a' }}>{'{Absenderadresse}'}</div>
        {isInline ? (
          <div style={{ 
            fontSize: '0.7em', 
            opacity: 0.7,
            fontWeight: fontWeight,
            fontStyle: fontStyle,
            textDecoration: fontDecoration
          }}>
            Firmenname, Straße + Hausnummer, PLZ Ort
          </div>
        ) : (
          <div style={{ fontSize: '0.7em', opacity: 0.7 }}>
            Firmenname<br />
            Straße + Hausnummer<br />
            PLZ Ort
          </div>
        )}
      </div>
    );
  }
}

export class RecipientRenderer extends BaseElementRenderer {
  canRender(elementType: string): boolean {
    return elementType === 'recipient';
  }
  
  render(context: ElementRenderContext) {
    return (
      <div 
        style={{
          ...context.elementStyle,
          backgroundColor: '#f0fdf4',
          border: '1px solid #86efac',
          borderRadius: '4px',
        }} 
        onClick={context.onClick}
      >
        <div style={{ color: '#16a34a' }}>{'{Empfängeradresse}'}</div>
        <div style={{ fontSize: '0.7em', opacity: 0.7 }}>
          Kundenname<br />
          Straße + Hausnummer<br />
          PLZ Ort
        </div>
      </div>
    );
  }
}

export class CustomerNumberRenderer extends BaseElementRenderer {
  canRender(elementType: string): boolean {
    return elementType === 'customer-number';
  }
  
  render(context: ElementRenderContext) {
    return this.renderPlaceholder(context, '{Kundennummer}', '#fef3c7', '#fde68a', '#ca8a04');
  }
}

// Bank Info Placeholders (Yellow)
export class IbanRenderer extends BaseElementRenderer {
  canRender(elementType: string): boolean {
    return elementType === 'iban';
  }
  
  render(context: ElementRenderContext) {
    return this.renderPlaceholder(context, '{IBAN}', '#fef9e7', '#fde68a', '#ca8a04');
  }
}

export class SwiftRenderer extends BaseElementRenderer {
  canRender(elementType: string): boolean {
    return elementType === 'swift';
  }
  
  render(context: ElementRenderContext) {
    return this.renderPlaceholder(context, '{SWIFT/BIC}', '#fef9e7', '#fde68a', '#ca8a04');
  }
}

export class BankNameRenderer extends BaseElementRenderer {
  canRender(elementType: string): boolean {
    return elementType === 'bank-name';
  }
  
  render(context: ElementRenderContext) {
    return this.renderPlaceholder(context, '{Bank}', '#fef9e7', '#fde68a', '#ca8a04');
  }
}

// Contact Info Placeholders (Purple)
export class EmailRenderer extends BaseElementRenderer {
  canRender(elementType: string): boolean {
    return elementType === 'email';
  }
  
  render(context: ElementRenderContext) {
    return this.renderPlaceholder(context, '{E-Mail}', '#ede9fe', '#c4b5fd', '#7c3aed');
  }
}

export class WebsiteRenderer extends BaseElementRenderer {
  canRender(elementType: string): boolean {
    return elementType === 'website';
  }
  
  render(context: ElementRenderContext) {
    return this.renderPlaceholder(context, '{Website}', '#ede9fe', '#c4b5fd', '#7c3aed');
  }
}

// Company Info Placeholders (Red)
export class CourtRenderer extends BaseElementRenderer {
  canRender(elementType: string): boolean {
    return elementType === 'court';
  }
  
  render(context: ElementRenderContext) {
    return this.renderPlaceholder(context, '{Amtsgericht}', '#fef3f2', '#fecaca', '#dc2626');
  }
}

export class HrnRenderer extends BaseElementRenderer {
  canRender(elementType: string): boolean {
    return elementType === 'hrn';
  }
  
  render(context: ElementRenderContext) {
    return this.renderPlaceholder(context, '{HR-Nr}', '#fef3f2', '#fecaca', '#dc2626');
  }
}

export class CountryRenderer extends BaseElementRenderer {
  canRender(elementType: string): boolean {
    return elementType === 'country';
  }
  
  render(context: ElementRenderContext) {
    return this.renderPlaceholder(context, '{Land}', '#fef3f2', '#fecaca', '#dc2626');
  }
}

export class TaxNumberRenderer extends BaseElementRenderer {
  canRender(elementType: string): boolean {
    return elementType === 'tax-number';
  }
  
  render(context: ElementRenderContext) {
    return this.renderPlaceholder(context, '{Steuernummer}', '#fef3f2', '#fecaca', '#dc2626');
  }
}

export class VatIdRenderer extends BaseElementRenderer {
  canRender(elementType: string): boolean {
    return elementType === 'vat-id';
  }
  
  render(context: ElementRenderContext) {
    return this.renderPlaceholder(context, '{Ust-IdNr}', '#fef3f2', '#fecaca', '#dc2626');
  }
}

// Composite Elements
export class InvoiceInfoBlockRenderer extends BaseElementRenderer {
  canRender(elementType: string): boolean {
    return elementType === 'invoice-info-block';
  }
  
  render(context: ElementRenderContext) {
    const element = context.element as any;
    const rows = element.rows || {};
    const labels = element.labels || {};
    const labelWidth = element.labelWidth || 150;
    const gap = element.gap || 1;
    
    // Get page number placeholder based on format
    const pageFormat = element.pageNumberFormat || 'page-x';
    let pagePlaceholder = '{1}';
    switch (pageFormat) {
      case 'page-x':
        pagePlaceholder = '{1}';
        break;
      case 'page-x-of-y':
        pagePlaceholder = '{1 von 2}';
        break;
      case 'x':
        pagePlaceholder = '{1}';
        break;
      case 'x-slash-y':
        pagePlaceholder = '{1/2}';
        break;
      case 'x-dash-y':
        pagePlaceholder = '{1-2}';
        break;
    }
    
    // Define all possible rows
    const rowDefinitions = [
      { key: 'invoiceNumber', defaultLabel: 'Rechnungsnummer', placeholder: '{Rechnungsnr.}' },
      { key: 'invoiceDate', defaultLabel: 'Rechnungsdatum', placeholder: '{Rechng.datum}' },
      { key: 'paymentDueDate', defaultLabel: 'Zahlungsbedingungen', placeholder: '{Zahlungsziel}' },
      { key: 'vatId', defaultLabel: 'Ust.-IdNr.', placeholder: '{Ust-IdNr}' },
      { key: 'customerNumber', defaultLabel: 'Kundennummer', placeholder: '{Kundennr.}' },
      { key: 'pageNumber', defaultLabel: 'Seite', placeholder: pagePlaceholder },
    ];
    
    // Filter visible rows
    const visibleRows = rowDefinitions.filter(row => rows[row.key] !== false);
    
    if (visibleRows.length === 0) {
      return this.renderPlaceholder(context, '{Rechnungsinfo-Block (keine Zeilen sichtbar)}', '#f3f4f6', '#d1d5db', '#6b7280');
    }
    
    return (
      <div 
        style={{
          ...context.elementStyle,
        }}
        onClick={context.onClick}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {visibleRows.map((row) => (
              <tr key={row.key}>
                <td 
                  style={{ 
                    width: `${labelWidth}px`,
                    padding: `${gap * 2}px ${gap * 4}px`,
                    fontWeight: 500,
                  }}
                >
                  {labels[row.key] || row.defaultLabel}:
                </td>
                <td 
                  style={{ 
                    padding: `${gap * 2}px ${gap * 4}px`,
                  }}
                >
                  {row.placeholder}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
}
